import React, { useEffect, useMemo, useRef, useState } from 'react';

// --- SVG Icons ---
const ChatIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
  </svg>
);

const CloseIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="28"
    height="28"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const SendIcon = ({ busy, disabled }) => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ opacity: disabled ? 0.5 : 1 }}
  >
    {busy ? (
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" strokeDasharray="3 3" >
         <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="1s" repeatCount="indefinite" />
      </circle>
    ) : (
      <path
        d="M22 2L11 13"
        stroke="white"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    )}
    <path
      d="M22 2L15 22L11 13L2 9L22 2Z"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

// --- Main Component ---
// ---- A/B test ---------------------------------------------------------------
//
// Assignment happens client-side because the greeting is shown before any
// session exists — the server has nothing to key off yet. The chosen arm is
// persisted so a returning visitor stays in the same one, and the server
// re-validates it (an unknown value falls back to control).
// A/B concluded 2026-07-29: the conversational 'human' (Sam) arm won on
// engagement (significant) and is the more genuine experience, so everyone now
// gets it. Leaving this a one-item list retires 'control' and re-rolls anyone
// still holding it (see getVariant) onto 'human'.
const AB_VARIANTS = ['human'];
const VARIANT_KEY = 'bfs_ab_variant';
const VISITOR_KEY = 'bfs_visitor_id';

// Page-aware openers. Each context has a greeting + a few tappable prompts that
// lower the barrier to that first message. Pages where we KNOW the worry get
// specific prompts; everything else (homepage, contact, general posts) gets the
// symptom-neutral set, so we never assume "twitching" for someone here about
// vibrations.
const CONTEXTS = {
  general: {
    greeting: "Hey, I'm Sam — I help folks here make sense of BFS. What's on your mind?",
    prompts: ['Is what I’m feeling normal?', 'Should I be worried?', 'How do I calm it down?'],
  },
  vibrations: {
    greeting: "Hey, I'm Sam. That buzzing or internal shakiness can be unsettling — want to make sense of it together?",
    prompts: ['Is this buzzing normal?', 'Why does it happen?', 'How do I calm it down?'],
  },
  als: {
    greeting: "Hey, I'm Sam. A lot of people here worry their symptoms mean something serious — want to talk it through?",
    prompts: ['Could this be ALS?', 'How is BFS different?', 'Should I see a doctor?'],
  },
  eye: {
    greeting: "Hey, I'm Sam. Eye twitching can be maddening — want to figure out what's behind it?",
    prompts: ['Why won’t my eye stop?', 'Is it something serious?', 'How do I make it stop?'],
  },
};

// Map the current path to a context. Order matters — check the most specific
// clusters first.
function contextForPath(pathname) {
  const p = (pathname || '').toLowerCase();
  if (/internal-vibration|internal-tremor|internal-shak|vibration/.test(p)) return 'vibrations';
  if (/vs-als|calf-twitching-als|tongue-fascic|perceived-weakness|no-weakness|one-spot|all-over|sensory/.test(p)) return 'als';
  if (/eye|eyelid|eyeball/.test(p)) return 'eye';
  return 'general';
}

function readStored(key, generate) {
  try {
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const fresh = generate();
    localStorage.setItem(key, fresh);
    return fresh;
  } catch (e) {
    // Private browsing / storage disabled: fall back to a value for this page
    // view only. The visitor still works, they just are not tracked across visits.
    return generate();
  }
}

function getVariant() {
  const roll = () => AB_VARIANTS[Math.floor(Math.random() * AB_VARIANTS.length)];
  const stored = readStored(VARIANT_KEY, roll);
  // A retired arm can linger in localStorage. Without this, those visitors are
  // stuck outside the experiment forever instead of being re-randomised.
  if (!AB_VARIANTS.includes(stored)) {
    const fresh = roll();
    try { localStorage.setItem(VARIANT_KEY, fresh); } catch (e) { /* ignore */ }
    return fresh;
  }
  return stored;
}

function getVisitorId() {
  return readStored(VISITOR_KEY, () =>
    (typeof crypto !== 'undefined' && crypto.randomUUID)
      ? crypto.randomUUID()
      : `v-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
  );
}

/**
 * Fire-and-forget funnel telemetry. Deliberately swallows every failure —
 * analytics must never be able to break the chat itself.
 */
function logChatEvent(event, sessionId, apiBase, variant, visitorId) {
  try {
    fetch(`${apiBase}/api/chat/event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event, sessionId: sessionId || null, variant, visitorId }),
      keepalive: true,
    }).catch(() => {});
  } catch (e) {
    /* ignore */
  }
}

// How the human arm paces its replies, to feel like a person: a beat of reading
// (nothing shown) → the typing dots → the reply. All measured from when the
// visitor sent, so it's consistent regardless of API speed.
const PACE = {
  readMinMs: 1500,   // reading beat before the typing dots even appear (from send)
  readMaxMs: 3000,
  firstMinMs: 5000,  // when the first reply bubble lands (from send)
  firstMaxMs: 10000,
  charMs: 28,        // ~per-character typing speed for later bubbles
  minPause: 900,     // floor per later bubble
  nextMax: 3500,     // cap on later bubbles
};

export default function ChatWidget({ onOpenChange, hidden = false }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState(''); // Used for chat message OR email
  const [nameInput, setNameInput] = useState(''); // NEW: Specifically for Name
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const [error, setError] = useState('');
  const [sessionId, setSessionId] = useState(null);
  const [isWaitingForLead, setIsWaitingForLead] = useState(false);
  // True while the human arm is pausing between bubbles, so the typing
  // indicator keeps running instead of the widget looking frozen.
  const [pacing, setPacing] = useState(false);
  // Small preview bubble shown instead of auto-opening the whole panel.
  const [showTeaser, setShowTeaser] = useState(false);

  const bottomRef = useRef(null);
  const hasAutoOpened = useRef(false);
  const inputRef = useRef(null);
  const originalQuestionRef = useRef(null);
  const hasLoggedOpen = useRef(false);
  const hasLoggedSend = useRef(false);
  const hasLoggedTeaser = useRef(false);

  // Resolved once per mount; both are stable across visits via localStorage.
  const variant = useMemo(() => getVariant(), []);
  const visitorId = useMemo(() => getVisitorId(), []);
  // The page's context decides the greeting and the suggested prompts.
  const ctx = useMemo(
    () => CONTEXTS[contextForPath(typeof window !== 'undefined' ? window.location.pathname : '')],
    []
  );
  // Suggested prompts show until the visitor sends their first message.

  // Update this URL if your API is on a different port/domain in production
  const apiBase = useMemo(() => 'https://benignfasciculationsyndrome.org', []);

  // Helper: Validates email format
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  // Effect: Initialize session / Load history
  useEffect(() => {
    const initializeSession = async () => {
      const storedSessionId = localStorage.getItem('chatSessionId');
      if (storedSessionId) {
        try {
          const res = await fetch(`${apiBase}/api/chat/history?sessionId=${storedSessionId}`);
          if (res.ok) {
            const data = await res.json();
            setMessages(data.messages || []);
            setSessionId(storedSessionId);
          } else {
             localStorage.removeItem('chatSessionId');
          }
        } catch (err) {
            console.error("Failed to fetch history:", err);
        }
      }
    };
    initializeSession();
  }, [apiBase]);

  // Effect: after a short delay, show a small teaser bubble instead of throwing
  // the whole panel open. Less intrusive, and "teaser shown -> tapped" becomes a
  // real intent signal rather than counting every page view as an "open".
  useEffect(() => {
    const timer = setTimeout(() => {
      if (hasAutoOpened.current || open || hidden) return;
      setShowTeaser(true);
      if (!hasLoggedTeaser.current) {
        hasLoggedTeaser.current = true;
        logChatEvent('teaser_shown', sessionId, apiBase, variant, visitorId);
      }
    }, 3000);
    return () => clearTimeout(timer);
  }, [open, hidden, sessionId, apiBase, variant, visitorId]);

  // Open the panel (from the teaser, the launcher, or a prompt tap). Seeds the
  // page-aware greeting on first open.
  const openChat = () => {
    setShowTeaser(false);
    hasAutoOpened.current = true;
    setMessages(prev => (prev.length === 0 ? [{ role: 'assistant', content: ctx.greeting }] : prev));
    setOpen(true);
  };

  // Tell the layout whether we are open, so the lead popup can hold off rather
  // than stealing focus mid-conversation.
  useEffect(() => {
    if (onOpenChange) onOpenChange(open);
  }, [open, onOpenChange]);

  // Effect: Log the first open of this page view. Nothing was recorded before
  // the visitor pressed send, so open -> type drop-off was invisible.
  useEffect(() => {
    if (!open || hasLoggedOpen.current) return;
    hasLoggedOpen.current = true;
    logChatEvent('widget_open', sessionId, apiBase, variant, visitorId);
  }, [open, sessionId, apiBase, variant, visitorId]);

  // Effect: Auto-scroll to bottom
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if(bottomRef.current) {
          bottomRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [messages, open]);

  /**
   * Reveal a reply the way a person would send it: a beat of thinking, then a
   * couple of short messages, rather than five paragraphs appearing at once.
   *
   * Total added latency is capped — past roughly four seconds this stops
   * reading as "someone is typing" and starts reading as "this is broken".
   */
  async function revealLikeAPerson(text, startedAt) {
    const sleep = ms => new Promise(r => setTimeout(r, ms));
    const clean = String(text || '').trim();
    if (!clean) return;
    const started = startedAt || Date.now();

    // Prefer paragraph breaks; fall back to sentences for a single long block.
    let parts = clean.split(/\n{2,}/).map(s => s.trim()).filter(Boolean);
    if (parts.length === 1 && clean.length > 180) {
      const sentences = clean.match(/[^.!?]+[.!?]+(\s|$)/g);
      if (sentences && sentences.length > 2) {
        const mid = Math.ceil(sentences.length / 2);
        parts = [sentences.slice(0, mid).join('').trim(), sentences.slice(mid).join('').trim()];
      }
    }
    parts = parts.slice(0, 3); // never more than three bubbles

    // Random targets for THIS reply, measured from when they sent.
    const readingEnd = PACE.readMinMs + Math.random() * (PACE.readMaxMs - PACE.readMinMs);
    const firstBubbleAt = PACE.firstMinMs + Math.random() * (PACE.firstMaxMs - PACE.firstMinMs);

    for (let i = 0; i < parts.length; i++) {
      if (i === 0) {
        // Reading beat — nothing shown (dots off) until readingEnd from send.
        setPacing(false);
        const readWait = readingEnd - (Date.now() - started);
        if (readWait > 0) await sleep(readWait);
        // Typing — dots on, until the first bubble is due.
        setPacing(true);
        const typeWait = firstBubbleAt - (Date.now() - started);
        if (typeWait > 0) await sleep(typeWait);
        setPacing(false);
      } else {
        // Later bubbles: dots, then reveal, at typing speed.
        const pause = Math.min(Math.max(PACE.minPause, parts[i].length * PACE.charMs), PACE.nextMax);
        setPacing(true);
        await sleep(pause);
        setPacing(false);
      }

      setMessages(prev => {
        const copy = [...prev];
        const last = copy.length - 1;
        // First part fills the empty placeholder bubble; the rest are new ones.
        if (i === 0 && last >= 0 && copy[last].role === 'assistant' && !copy[last].content) {
          copy[last] = { ...copy[last], content: parts[i] };
          return copy;
        }
        return [...copy, { role: 'assistant', content: parts[i] }];
      });
    }
  }

  // --- API Handling ---
  async function streamResponse(payload) {
    const startedAt = Date.now(); // for pacing the first reply bubble
    setBusy(true);
    try {
      const res = await fetch(`${apiBase}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      // Handle the 403 "Hard Gate"
      if (res.status === 403) {
        const data = await res.json(); 
        
        if (data.sessionId) {
            setSessionId(data.sessionId);
            localStorage.setItem('chatSessionId', data.sessionId);
        }

        if (variant === 'human') {
          // No form in this arm. This 403 is only the spend backstop, so ask in
          // the thread — the server picks the address out of their next message.
          setMessages(prev => [...prev.slice(0, -1), {
            role: 'assistant',
            content: "By the way — we put together a free 14-Day Calm Protocol that's helped a lot of people here settle this down. Want me to email it to you? Just pop your address in.",
          }]);
        } else {
          setIsWaitingForLead(true);
          setMessages(prev => [...prev.slice(0, -1), {
            role: 'assistant',
            content: "I'd love to keep helping. Pop in your name and email and I'll continue right where we left off — plus I'll send you the free 14-Day BFS Calm Protocol.",
          }]);
        }
        setBusy(false);
        return; // Stop here, wait for user input
      }

      if (!res.ok || !res.body) throw new Error('No stream body');
      
      // Update session ID if header is present
      const newSessionId = res.headers.get('X-Session-Id');
      if (newSessionId && newSessionId !== sessionId) {
          setSessionId(newSessionId);
          localStorage.setItem('chatSessionId', newSessionId);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let done = false;
      let full = '';

      while (!done) {
        const { value, done: readerDone } = await reader.read();
        done = readerDone;
        const chunk = decoder.decode(value || new Uint8Array(), { stream: !done });
        if (!chunk) continue;
        full += chunk;

        // Control arm keeps the original token-by-token reveal. The human arm
        // buffers instead, so it can be paced out below.
        if (variant !== 'human') {
          setMessages(prev => {
            const copy = [...prev];
            const i = copy.length - 1;
            if (i >= 0 && copy[i].role === 'assistant') {
              copy[i] = { ...copy[i], content: copy[i].content + chunk };
            }
            return copy;
          });
        }
      }

      if (variant === 'human') await revealLikeAPerson(full, startedAt);
    } catch (err) {
      console.error(err);
      setError('Sorry—something went wrong. Please try again.');
      setMessages(prev => [...prev.slice(0, -1), { role: 'assistant', content: 'Sorry—something went wrong. Please try again.' }]);
    } finally {
      setBusy(false);
    }
  }

  async function handleSendMessage(e, presetText) {
    if (e) e.preventDefault();
    // presetText comes from tapping a suggested prompt; otherwise use the box.
    const text = (presetText != null ? presetText : input).trim();

    if (isWaitingForLead) {
        // --- Lead Capture Logic ---
        // Validate both Name and Email
        if (!nameInput.trim() || !isValidEmail(text)) {
            setError("Please provide both your name and a valid email.");
            return;
        }

        setBusy(true);
        setError(''); 
        try {
            const leadRes = await fetch(`${apiBase}/api/leads/save`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    name: nameInput.trim(), 
                    email: text,
                    sessionId,
                })
            });

            if (!leadRes.ok) throw new Error('Failed to save lead');
            
            // Success! Resume chat
            setIsWaitingForLead(false);
            setInput('');
            setNameInput('');
            
            // Add placeholder for the answer
            setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
            
            // Resend the original question
            const payload = { 
                sessionId, 
                variant,
                visitorId,
                messages: [...messages, {role: 'user', content: originalQuestionRef.current}], 
            };
            streamResponse(payload);

        } catch (err) {
            console.error(err);
            setError('Could not verify details. Please try again.');
            setBusy(false);
        }
        return;
    }

    // --- Standard Chat Logic ---
    if (!text || busy) return;

    if (!hasLoggedSend.current) {
      hasLoggedSend.current = true;
      logChatEvent('first_send', sessionId, apiBase, variant, visitorId);
    }

    originalQuestionRef.current = text;
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
    
    const payload = { sessionId, variant, visitorId, messages: [...messages, {role: 'user', content: text}] };
    streamResponse(payload);
  }


  // Hidden (not unmounted) while the lead popup is up — unmounting would throw
  // away an in-progress conversation.
  if (hidden) {
    return <div style={{ display: 'none' }} aria-hidden="true" />;
  }

  return (
    <>
      {/* Teaser: a small preview that invites a tap, instead of auto-opening the
          whole panel. Dismissible; tapping it opens the chat. */}
      {showTeaser && !open && (
        <div style={styles.teaser}>
          <button
            type="button"
            style={styles.teaserClose}
            aria-label="Dismiss"
            onClick={(e) => { e.stopPropagation(); setShowTeaser(false); hasAutoOpened.current = true; }}
          >
            ×
          </button>
          <div style={styles.teaserBody} onClick={openChat} role="button" tabIndex={0}>
            <div style={styles.teaserName}>Sam · BFS Support</div>
            <div style={styles.teaserMsg}>{ctx.greeting}</div>
            <div style={styles.teaserCta}>Tap to chat →</div>
          </div>
        </div>
      )}

      <button
        onClick={() => (open ? setOpen(false) : openChat())}
        style={{...styles.fab, ...(open ? styles.fabOpen : {})}}
        aria-label="Open BFS chat"
        title={open ? 'Close chat' : 'Chat with Sam'}
      >
        {open ? <CloseIcon /> : <ChatIcon />}
      </button>

      {open && (
        <div style={styles.panel} role="dialog" aria-label="BFS Support">
          <div style={styles.header}>
              <div style={styles.headerTitle}>Sam · BFS Support</div>
              {/* Honest presence signal: an AI genuinely is always available. */}
              <div style={styles.headerStatus}>
                <span style={styles.onlineDotInline} /> Online · usually replies instantly
              </div>
              <div style={styles.headerSubtitle}>Educational only — not medical advice</div>
          </div>

          <div style={styles.messagesContainer}>
            <div style={styles.messages} aria-live="polite">
                {messages.map((m, i) => (
                <div
                    key={i}
                    style={{
                    ...styles.bubble,
                    ...(m.role === 'user' ? styles.userBubble : styles.assistantBubble)
                    }}
                >
                    {m.content}
                </div>
                ))}
                {/* Dots show only when `pacing` is on — which starts AFTER the
                    reading beat, so they no longer appear the instant you send. */}
                {pacing &&
                  <div style={{...styles.bubble, ...styles.assistantBubble, ...styles.typingBubble}}>
                    <span style={{...styles.typingDot, animationDelay:'0s'}}></span>
                    <span style={{...styles.typingDot, animationDelay:'0.2s'}}></span>
                    <span style={{...styles.typingDot, animationDelay:'0.4s'}}></span>
                  </div>
                }

                {/* One-tap starters — shown until the visitor sends anything.
                    Removes the "what do I even ask" barrier, especially on mobile. */}
                {!isWaitingForLead && !messages.some(m => m.role === 'user') && (
                  <div style={styles.prompts}>
                    {ctx.prompts.map((q) => (
                      <button
                        key={q}
                        type="button"
                        style={styles.promptChip}
                        onClick={() => handleSendMessage(null, q)}
                        disabled={busy}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                )}
                <div ref={bottomRef} />
            </div>
          </div>
          
          {error && <div style={styles.error}>{error}</div>}

          {/* Conditional Rendering: Lead Form vs Chat Input */}
          {isWaitingForLead ? (
             <div style={styles.leadFormContainer}>
               <div style={styles.leadText}>Enter your details to continue the live chat:</div>
               <div style={styles.leadFields}>
                 {/* Name Input */}
                 <input 
                   type="text" 
                   value={nameInput}
                   onChange={e => setNameInput(e.target.value)}
                   placeholder="Your Name"
                   style={styles.input}
                   disabled={busy}
                 />
                 {/* Email Input */}
                 <input
                   type="email"
                   value={input}
                   onChange={e => setInput(e.target.value)}
                   placeholder="email@example.com"
                   style={styles.input}
                   disabled={busy}
                 />
               </div>
               <button 
                 onClick={handleSendMessage} 
                 disabled={!isValidEmail(input) || !nameInput.trim() || busy}
                 style={{...styles.unlockButton}}
               >
                  {busy ? 'Verifying...' : 'Submit'}
               </button>
             </div>
          ) : (
            <div>
              <form style={styles.inputRow} onSubmit={handleSendMessage}>
                  <input
                  ref={inputRef}
                  type="text"
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  placeholder="Type your question…"
                  style={styles.input}
                  disabled={busy}
                  aria-label="Message input"
                  />
                  <button type="submit" style={{...styles.send, ...( (busy || !input.trim()) ? styles.sendDisabled : {})}} disabled={busy || !input.trim()}>
                  <SendIcon busy={busy} disabled={busy || !input.trim()} />
                  </button>
              </form>
              {/* Persistent, non-blocking disclosure — replaces the click-through
                  modal that was gating out 56% of people who opened the chat. */}
              <div style={styles.inputNotice}>
                Educational only — not medical advice.
              </div>
            </div>
          )}
        </div>
      )}
      <style>{`
        @keyframes fadeIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
        }
        @keyframes typing {
            0%, 60%, 100% { transform: translateY(0); }
            30% { transform: translateY(-4px); }
        }
      `}</style>
    </>
  );
}

const BRAND_COLOR = '#3b82f6';

const styles = {
  teaser: {
    position: 'fixed',
    right: 20,
    bottom: 92,
    zIndex: 9999,
    width: 'min(78vw, 300px)',
    background: '#fff',
    borderRadius: 16,
    boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
    border: '1px solid #e5e7eb',
    animation: 'fadeIn 0.35s ease-out',
  },
  teaserBody: { padding: '14px 16px 12px', cursor: 'pointer' },
  teaserName: { fontSize: 12, fontWeight: 700, color: '#0a4b5e', marginBottom: 4 },
  teaserMsg: { fontSize: 14, lineHeight: 1.4, color: '#1f2937' },
  teaserCta: { marginTop: 8, fontSize: 12.5, fontWeight: 600, color: BRAND_COLOR },
  teaserClose: {
    position: 'absolute', top: 6, right: 8, width: 26, height: 26,
    border: 'none', background: 'transparent', color: '#9ca3af',
    fontSize: 20, lineHeight: 1, cursor: 'pointer', borderRadius: 6,
  },
  prompts: { display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start', marginTop: 4 },
  promptChip: {
    textAlign: 'left', padding: '9px 14px', borderRadius: 16,
    border: `1px solid ${BRAND_COLOR}`, background: '#eff6ff', color: '#1e3a8a',
    fontSize: 13.5, fontWeight: 500, cursor: 'pointer', maxWidth: '90%',
  },
  fab: {
    position: 'fixed',
    right: 20,
    bottom: 20,
    zIndex: 9999,
    width: 60,
    height: 60,
    borderRadius: '50%',
    border: 'none',
    boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
    background: BRAND_COLOR,
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s ease-in-out, background-color 0.2s',
  },
  fabOpen: {
      transform: 'scale(0.9)',
      backgroundColor: '#9ca3af'
  },
  fabAvatar: {
    background: 'transparent',
    padding: 0,
    overflow: 'visible',
  },
  fabAvatarWrap: {
    position: 'relative',
    display: 'inline-flex',
    lineHeight: 0,
  },
  fabOnlineDot: {
    position: 'absolute',
    right: 2,
    bottom: 2,
    width: 14,
    height: 14,
    borderRadius: '50%',
    background: '#22c55e',
    border: '3px solid #fff',
  },
  panel: {
    position: 'fixed',
    right: 20,
    bottom: 95,
    width: 'min(90vw, 380px)',
    maxHeight: 'min(75vh, 600px)',
    background: '#f9fafb',
    borderRadius: 20,
    overflow: 'hidden',
    boxShadow: '0 16px 48px rgba(0,0,0,0.2)',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 9999,
    border: '1px solid #e5e7eb',
    animation: 'fadeIn 0.3s ease-out'
  },
  header: {
    padding: '16px 20px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fff',
    flexShrink: 0,
  },
  headerAvatarWrap: {
    position: 'relative',
    flexShrink: 0,
    lineHeight: 0,
  },
  onlineDot: {
    position: 'absolute',
    right: 0,
    bottom: 2,
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: '#22c55e',
    border: '2px solid #fff',
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: '#1f2937',
  },
  headerStatus: {
    display: 'flex',
    alignItems: 'center',
    gap: 5,
    marginTop: 2,
    fontSize: 12,
    color: '#16a34a',
    fontWeight: 500,
  },
  onlineDotInline: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: '#22c55e',
    display: 'inline-block',
  },
  headerSubtitle: {
    marginTop: 3,
    fontSize: 11,
    color: '#9ca3af',
  },
  assistantRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: 8,
    alignSelf: 'flex-start',
    maxWidth: '90%',
  },
  msgAvatar: {
    flexShrink: 0,
    lineHeight: 0,
    marginBottom: 2,
  },
  error: {
    padding: '8px 20px',
    fontSize: 13,
    color: '#b91c1c',
    background: '#fee2e2',
    textAlign: 'center'
  },
  messagesContainer: {
    flex: 1,
    overflowY: 'auto',
    position: 'relative',
  },
  messages: {
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  bubble: {
    padding: '12px 16px',
    borderRadius: 18,
    lineHeight: 1.5,
    fontSize: 14,
    maxWidth: '85%',
    whiteSpace: 'pre-wrap',
    wordWrap: 'break-word',
  },
  userBubble: {
    alignSelf: 'flex-end',
    background: BRAND_COLOR,
    color: '#fff',
    borderRadius: '18px 18px 4px 18px',
  },
  assistantBubble: {
    alignSelf: 'flex-start',
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    color: '#374151',
    borderRadius: '18px 18px 18px 4px',
  },
  typingBubble: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    padding: '16px 20px',
  },
  typingDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: '#9ca3af',
    animation: 'typing 1.2s infinite ease-in-out',
  },
  inputRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 12,
    borderTop: '1px solid #e5e7eb',
    background: '#fff'
  },
  inputNotice: {
    fontSize: 11,
    color: '#9ca3af',
    textAlign: 'center',
    padding: '0 12px 8px',
    background: '#fff',
  },
  leadFormContainer: {
    padding: 15, 
    background: '#eff6ff', 
    borderTop: '1px solid #3b82f6',
    display: 'flex',
    flexDirection: 'column',
    gap: 10
  },
  leadText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#1e40af',
    marginBottom: 4
  },
  leadFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8
  },
  input: {
    flex: 1,
    padding: '10px 14px',
    borderRadius: 12,
    border: '1px solid #d1d5db',
    fontSize: 14,
    outline: 'none',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  },
  unlockButton: {
    width: '100%', 
    padding: '10px', 
    background: BRAND_COLOR, 
    color: '#fff', 
    border: 'none', 
    borderRadius: 8, 
    cursor: 'pointer',
    fontWeight: '600',
    marginTop: 5
  },
  send: {
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: 'none',
    background: BRAND_COLOR,
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.2s, opacity 0.2s',
    flexShrink: 0,
  },
  sendDisabled: {
      backgroundColor: '#a5b4fc',
      cursor: 'not-allowed',
  },
  disclaimerOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: 20,
  },
  disclaimerModal: {
    background: '#fff',
    padding: 24,
    borderRadius: 16,
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
    maxWidth: '90%',
    width: '400px',
  },
  disclaimerText: {
    marginTop: 12,
    fontSize: 13,
    color: '#4b5563',
    lineHeight: 1.6,
    maxHeight: '40vh',
    overflowY: 'auto'
  },
  disclaimerButton: {
    marginTop: 20,
    padding: '12px 20px',
    borderRadius: 12,
    border: 'none',
    background: BRAND_COLOR,
    color: '#fff',
    fontWeight: 'bold',
    fontSize: 16,
    cursor: 'pointer',
    width: '100%',
    transition: 'background-color 0.2s',
  },
};